import React, { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuoteModal } from "../context/QuoteModalContext";
import LogoMark from "./LogoMark";
import { siteConfig } from "../siteConfig";
import { submitLead } from "../submitLead";

/*
  Quote popup modal.

  Two modes:
  1. Native form (default) — placeholder form that submits to mailto or a handler.
  2. GHL iframe — set siteConfig.ghlPopupFormUrl to your GoHighLevel form URL
     and the iframe replaces the native form automatically.
*/

const NativeForm = ({ onSuccess }) => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Posts to GHL via siteConfig.ghlWebhookUrl (see src/submitLead.js).
    await submitLead(form);
    setSubmitting(false);
    setSubmitted(true);
    onSuccess?.();
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: `${siteConfig.colors.primary}20` }}
        >
          <Send className="w-7 h-7" style={{ color: siteConfig.colors.primary }} />
        </div>
        <h3 className="font-display font-black text-white text-xl mb-2">We got it!</h3>
        <p className="text-white/60 text-sm">
          We'll reach out to you shortly at {form.phone || form.name}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Full Name */}
      <div>
        <label className="block text-sm font-bold text-white mb-1.5">
          Full Name <span style={{ color: siteConfig.colors.primary }}>*</span>
        </label>
        <input
          type="text"
          required
          placeholder="John Smith"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className="w-full h-12 px-4 rounded-lg bg-white text-gray-900 text-sm placeholder-gray-400 outline-none focus:ring-2"
          style={{ "--tw-ring-color": siteConfig.colors.primary }}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-sm font-bold text-white mb-1.5">Phone</label>
        <div className="flex items-center h-12 rounded-lg bg-white overflow-hidden">
          {/* Flag prefix */}
          <div className="flex items-center gap-1.5 pl-3 pr-2 border-r border-gray-200 shrink-0">
            <span className="text-lg leading-none">🇺🇸</span>
            <span className="text-gray-400 text-xs">▾</span>
          </div>
          <input
            type="tel"
            placeholder="Example: (808) 555-1234"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            className="flex-1 h-full px-3 text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
          />
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm font-bold text-white mb-1.5">
          Short message about your needs <span style={{ color: siteConfig.colors.primary }}>*</span>
        </label>
        <textarea
          required
          rows={4}
          placeholder="**Your message goes straight to my phone, I'll get back to you as soon as I'm available**"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 text-sm placeholder-gray-400 outline-none resize-none focus:ring-2"
          style={{ "--tw-ring-color": siteConfig.colors.primary }}
        />
      </div>

      {/* Consent disclaimer — no checkbox */}
      <p className="text-xs text-white/50 leading-relaxed">
        By submitting this form I agree to{" "}
        <Link
          to="/terms"
          className="underline hover:text-white/70 transition-colors"
          style={{ color: siteConfig.colors.primary }}
        >
          terms &amp; conditions
        </Link>{" "}
        provided by {siteConfig.businessName}. By providing my phone number, I agree
        to receive text messages from {siteConfig.businessName}.
      </p>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full h-14 rounded-lg text-white font-black uppercase tracking-widest text-sm transition-opacity hover:opacity-90 disabled:opacity-60 mt-1"
        style={{ backgroundColor: siteConfig.colors.primary, letterSpacing: "0.1em" }}
      >
        {submitting ? "Sending…" : "Send"}
      </button>
    </form>
  );
};

const QuoteModal = () => {
  const { isOpen, close } = useQuoteModal();
  const overlayRef = useRef(null);
  const hasGHL = Boolean(siteConfig.ghlPopupFormUrl);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, close]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.82)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === overlayRef.current) close(); }}
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl p-8 shadow-2xl"
        style={{ backgroundColor: siteConfig.colors.dark }}
      >
        {/* Close button */}
        <button
          onClick={close}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/50 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-5">
          <LogoMark scrolled={false} />
        </div>

        {/* Heading */}
        <h2
          className="text-center font-display font-black text-white uppercase mb-6"
          style={{ fontSize: "clamp(1.5rem,4vw,2rem)", letterSpacing: "0.04em" }}
        >
          Get a Free Quote
        </h2>

        {/* Form — GHL iframe or native */}
        {hasGHL ? (
          <iframe
            src={siteConfig.ghlPopupFormUrl}
            title="Get a Free Quote"
            className="w-full rounded-lg"
            style={{ minHeight: 520, border: 0 }}
            loading="lazy"
          />
        ) : (
          <NativeForm onSuccess={close} />
        )}
      </div>
    </div>
  );
};

export default QuoteModal;
